
// Ren layout-logik for labels (ingen THREE, ingen DOM): niveauer, prioritet, kandidatplaceringer og overlap i SKÆRMRUM (px).
// Testet i labelLayout.test.mjs. LabelManager.js leverer projicerede ankre/størrelser og anvender resultatet.
//
// Tre uafhængige spørgsmål pr. label (se LABEL-STRATEGI.md):
//   A) må den være synlig  - zoom-/pladsregler her + brugerens eget valg (ejes af FeatureVisual, ikke af denne fil)
//   B) hvor øverst tegnes den - renderOrder, ejes af LabelManager (tier -> renderOrder)
//   C) hvor meget indhold      - LEVEL: fuld / reduceret / mindste / skjult. Trappen leveres af ejeren (værktøjet); her vælges kun det
//      rigeste trin, der kan stå frit. Et resultat uden egen reduceret udgave er enten fuldt eller skjult.
// Adgang til PLADS afgøres her, i rækkefølgen tier -> tidligere synlig -> prioritet (fx objektstørrelse) -> nærhed til midten.
// Ingen antalskvoter: pladsen (overlap) er den eneste naturlige grænse.

export const LEVEL = Object.freeze({HIDDEN: 0, COMPACT: 1, MEDIUM: 2, FULL: 3});

// Prioritet ved pladskonflikt (lavest tal først):
//   0 nødvendige arbejdsoplysninger under oprettelse/redigering, 1 primært valgt objekts resultat, 2 hover og øvrige valgte objekters resultater,
//   3 almindelige hovedresultater (værktøjernes resultater, rør), 4 objektidentifikation (navn/rolle), 5 delmål og hjælpeoplysninger,
//   6 punktkoordinater (udfases først)
export const TIER = Object.freeze({ACTIVE: 0, PRIMARY: 1, HOVER: 2, ORDINARY: 3, IDENT: 4, SECONDARY: 5, TERTIARY: 6});

// Startværdier (kalibreres ved afprøvning, se LABEL-STRATEGI.md). Alle afstande er CSS-px.
export const RULES = Object.freeze({
	collapsedPx: 2, // et objekt, der er skrumpet til under dette (et punkt på skærmen), har intet at pege på: resultatet skjules. Ingen anden størrelsesregel for resultater
	pointMinPpm: 6, // punktkoordinater: under denne px pr. meter ved punktet svarer en pixel til > 17 cm, og koordinaten kan ikke knyttes til punktet
	hysteresis: 0.8, // en regel forlades først ved 0,8 x tærsklen
	edgeFit: 1.1, // en delmåls-label kræver, at kanten er mindst 1,1 x labelbredden ...
	edgePad: 12, // ... plus denne luft
	gap: 6, // luft mellem label og linje
	ring1: 8, // første ring af kandidater omkring et punkt-anker
	ring2: 26, // anden ring (med forbindelseslinje): aktiv, valgt, hover og resultater
	ring3: 48, // yderste ring - kun aktiv, valgt og hover
	leaderMin: 12, // forbindelseslinje, når labelen ligger længere end dette fra ankeret
	margin: 8, // luft til arbejdsfladens kant
	pad: 2, // luft mellem labels
	reshowPad: 4, // ekstra luft, før en skjult/nedgraderet label vender tilbage (hindrer flimmer)
	handleRadius: 8, // betjeningsgreb (vertex-kugle) regnes for en cirkel af denne radius
	anchorInset: 8, // et anker anses for synligt, når det ligger mindst så langt inde i arbejdsfladen (24 px, før et flyttet anker vender tilbage til sit eget sted)
	anchorReturnInset: 24,
	anchorMinPx: 16, // synligt stykke geometri (px), der skal til for at et resultat kan forankres til den synlige del af et objekt, hvis eget anker er uden for billedet
	quietRadius: 60, // stille zone (px) om det punkt, der trækkes: øvrige labels viger
});

// Klassifikation af en labels FUNKTION. Et resultat (værktøjets samlede resultatboks) får altid samme prioritet og synlighed som én enhed, uanset
// hvilken klasse der ejer det (FeatureVisual.resultLabel, PipeVisual.label ...): ejeren erklærer rollen "result" (labelState.setLabelRole), og begge
// indsamlere bruger denne ene funktion. ctx: {working, selectedPrimary, selectedOther, hovered}. Returnerer {tier, forced}.
export function resultClass(ctx){
	if(ctx.working) return {tier: TIER.ACTIVE, forced: true};
	if(ctx.selectedPrimary) return {tier: TIER.PRIMARY, forced: true};
	if(ctx.selectedOther || ctx.hovered) return {tier: TIER.HOVER, forced: false};
	return {tier: TIER.ORDINARY, forced: false};
}

const GRID_CELL = 64;

// Større end en "plet"? Hysterese: et synligt objekt forlades først ved h x tærsklen.
export function visibleAbove(prevVisible, metric, threshold, h = RULES.hysteresis){
	return metric >= (prevVisible ? threshold * h : threshold);
}

// Kan en delmåls-label (bredde labelW) stå ved en kant på edgePx? Hysterese ved prevOn.
export function edgeFits(prevOn, edgePx, labelW, rules = RULES){
	const threshold = labelW * rules.edgeFit + rules.edgePad;
	return edgePx >= (prevOn ? threshold * rules.hysteresis : threshold);
}

// ---------------------------------------------------------------------------------------------------------------------
// Geometri-hjælp (rektangel = {x0, y0, x1, y1})

function rectsOverlapArea(ax0, ay0, ax1, ay1, bx0, by0, bx1, by1){
	const w = Math.min(ax1, bx1) - Math.max(ax0, bx0);
	if(w <= 0) return 0;
	const h = Math.min(ay1, by1) - Math.max(ay0, by0);
	return h <= 0 ? 0 : w * h;
}

// Skærer segmentet (x0,y0)-(x1,y1) rektanglet? (slab-test)
function segmentHitsRect(x0, y0, x1, y1, rx0, ry0, rx1, ry1){
	const dx = x1 - x0, dy = y1 - y0;
	let t0 = 0, t1 = 1;
	// fire kanter (venstre, højre, top, bund): p = retningens komponent mod kanten, q = afstand til kanten
	for(let i = 0; i < 4; i++){
		let p, q;
		if(i === 0){ p = -dx; q = x0 - rx0; }
		else if(i === 1){ p = dx; q = rx1 - x0; }
		else if(i === 2){ p = -dy; q = y0 - ry0; }
		else { p = dy; q = ry1 - y0; }
		if(p === 0){
			if(q < 0) return false;
		}else{
			const r = q / p;
			if(p < 0){
				if(r > t1) return false;
				if(r > t0) t0 = r;
			}else{
				if(r < t0) return false;
				if(r < t1) t1 = r;
			}
		}
	}
	return true;
}

function circleHitsRect(cx, cy, r, rx0, ry0, rx1, ry1){
	const nx = Math.max(rx0, Math.min(cx, rx1));
	const ny = Math.max(ry0, Math.min(cy, ry1));
	const dx = cx - nx, dy = cy - ny;
	return dx * dx + dy * dy <= r * r;
}

// Afstand fra et punkt til et rektangel (0 inde i det)
export function pointRectDistance(px, py, rx0, ry0, rx1, ry1){
	const dx = Math.max(rx0 - px, 0, px - rx1);
	const dy = Math.max(ry0 - py, 0, py - ry1);
	return Math.sqrt(dx * dx + dy * dy);
}

// ---------------------------------------------------------------------------------------------------------------------
// Rumligt gitter over objekternes skærmgeometri (linjestykker og betjeningsgreb). Fladt lager og genbrugte arrays: opbygges
// højst ved hvert layout, uden en allokering pr. element.

export class GeometryGrid {
	constructor(){
		this.type = []; // 0 = linjestykke, 1 = cirkel
		this.owner = [];
		this.a = []; this.b = []; this.c = []; this.d = [];
		this.count = 0;
		this.cells = new Map();
		this._pool = [];
		this._stamp = 0;
		this._seen = [];
	}

	clear(){
		for(const list of this.cells.values()){
			list.length = 0;
			this._pool.push(list);
		}
		this.cells.clear();
		this.count = 0;
	}

	_cellList(ix, iy, create){
		const key = (ix + 32768) * 65536 + (iy + 32768);
		let list = this.cells.get(key);
		if(!list && create){
			list = this._pool.pop() || [];
			this.cells.set(key, list);
		}
		return list;
	}

	_add(type, owner, a, b, c, d){
		const i = this.count++;
		this.type[i] = type; this.owner[i] = owner;
		this.a[i] = a; this.b[i] = b; this.c[i] = c; this.d[i] = d;
		return i;
	}

	addSegment(owner, x0, y0, x1, y1){
		if(!isFinite(x0 + y0 + x1 + y1)) return;
		const i = this._add(0, owner, x0, y0, x1, y1);
		this._rasterize(i, Math.min(x0, x1), Math.min(y0, y1), Math.max(x0, x1), Math.max(y0, y1), x0, y0, x1, y1);
	}

	addCircle(owner, x, y, r){
		if(!isFinite(x + y)) return;
		const i = this._add(1, owner, x, y, r, 0);
		this._rasterize(i, x - r, y - r, x + r, y + r, null);
	}

	_rasterize(i, minX, minY, maxX, maxY, x0, y0, x1, y1){
		const ix0 = Math.floor(minX / GRID_CELL), ix1 = Math.floor(maxX / GRID_CELL);
		const iy0 = Math.floor(minY / GRID_CELL), iy1 = Math.floor(maxY / GRID_CELL);
		// meget store udstrækninger (linjer langt uden for skærmen) begrænses til et fornuftigt antal celler
		if((ix1 - ix0 + 1) * (iy1 - iy0 + 1) > 400){
			this.count--;
			return;
		}
		const isSeg = x0 !== null && x0 !== undefined;
		for(let ix = ix0; ix <= ix1; ix++){
			for(let iy = iy0; iy <= iy1; iy++){
				if(isSeg && (ix0 !== ix1 || iy0 !== iy1)){
					// kun celler, som linjestykket faktisk berører
					const cx0 = ix * GRID_CELL, cy0 = iy * GRID_CELL;
					if(!segmentHitsRect(x0, y0, x1, y1, cx0, cy0, cx0 + GRID_CELL, cy0 + GRID_CELL)) continue;
				}
				this._cellList(ix, iy, true).push(i);
			}
		}
	}

	// Kalder visit(index) for hvert element, hvis celle berører rektanglet (højst én gang pr. element).
	// Returnerer ikke noget; kalderen læser type/owner/a-d.
	query(rx0, ry0, rx1, ry1, visit){
		const stamp = ++this._stamp;
		const ix0 = Math.floor(rx0 / GRID_CELL), ix1 = Math.floor(rx1 / GRID_CELL);
		const iy0 = Math.floor(ry0 / GRID_CELL), iy1 = Math.floor(ry1 / GRID_CELL);
		for(let ix = ix0; ix <= ix1; ix++){
			for(let iy = iy0; iy <= iy1; iy++){
				const list = this._cellList(ix, iy, false);
				if(!list) continue;
				for(let k = 0; k < list.length; k++){
					const i = list[k];
					if(this._seen[i] === stamp) continue;
					this._seen[i] = stamp;
					visit(i);
				}
			}
		}
	}
}

// ---------------------------------------------------------------------------------------------------------------------
// Kandidatplaceringer

const MAX_CANDIDATES = 28;
const candidates = [];
for(let i = 0; i < MAX_CANDIDATES; i++) candidates.push({cx: 0, cy: 0, key: "", leader: false});

function pushCandidate(n, cx, cy, key){
	const c = candidates[n];
	c.cx = cx; c.cy = cy; c.key = key; c.leader = false;
	return n + 1;
}

// Ring af otte pladser omkring et punkt-anker; den mindste afstand fra ankeret til labelens kant er ca. g.
function pushRing(n, ax, ay, hw, hh, g, suffix){
	n = pushCandidate(n, ax + hw + g, ay - hh - g, "NE" + suffix);
	n = pushCandidate(n, ax - hw - g, ay - hh - g, "NW" + suffix);
	n = pushCandidate(n, ax + hw + g, ay + hh + g, "SE" + suffix);
	n = pushCandidate(n, ax - hw - g, ay + hh + g, "SW" + suffix);
	n = pushCandidate(n, ax, ay - hh - g, "N" + suffix);
	n = pushCandidate(n, ax, ay + hh + g, "S" + suffix);
	n = pushCandidate(n, ax + hw + g, ay, "E" + suffix);
	n = pushCandidate(n, ax - hw - g, ay, "W" + suffix);
	return n;
}

// Pladser langs en kant: midt for (begge sider), derefter forskudt mod enderne
const SEGMENT_SLOTS = [
	{t: 0.5, side: 1, key: "t50+"}, {t: 0.5, side: -1, key: "t50-"},
	{t: 0.3, side: 1, key: "t30+"}, {t: 0.7, side: 1, key: "t70+"},
	{t: 0.3, side: -1, key: "t30-"}, {t: 0.7, side: -1, key: "t70-"},
];

// Kandidater for en label af størrelse size ({w,h}). Den foretrukne først. Returnerer antallet.
export function buildCandidates(rec, size, rules = RULES){
	const hw = size.w / 2, hh = size.h / 2;
	let n = 0;

	if(rec.kind === "segment" && rec.edge){
		const {x0, y0, x1, y1} = rec.edge;
		const dx = x1 - x0, dy = y1 - y0;
		const len = Math.hypot(dx, dy) || 1;
		let nx = -dy / len, ny = dx / len;
		// normalen peger "op" på skærmen (og mod højre ved en vandret kant), så labelen altid ligger samme side af kanten
		if(ny > 1e-6 || (Math.abs(ny) <= 1e-6 && nx < 0)){ nx = -nx; ny = -ny; }
		const extent = Math.abs(nx) * hw + Math.abs(ny) * hh + rules.gap;
		for(let k = 0; k < SEGMENT_SLOTS.length; k++){
			const slot = SEGMENT_SLOTS[k];
			n = pushCandidate(n, x0 + dx * slot.t + nx * extent * slot.side, y0 + dy * slot.t + ny * extent * slot.side, slot.key);
		}
		return n; // knyttet til kanten ved nærhed: ingen forbindelseslinje
	}

	// resultater, rørets resultat og hjælpelabels står først på deres eget anker (hvor ejeren har lagt dem); først ved en reel konflikt flyttes de
	if(rec.kind === "result" || rec.kind === "pipe" || rec.anchorOnly) n = pushCandidate(n, rec.ax, rec.ay, "C");
	n = pushRing(n, rec.ax, rec.ay, hw, hh, rules.ring1, "");
	// større forskydning og forbindelseslinjer: aktiv handling, valg og hover (tier 0-2) får begge ringe; resultater (tier 3) den første af dem,
	// fordi et resultat skal kunne aflæses; delmål, hjælp og koordinater (tier 4-5) reduceres eller skjules i stedet for at flyttes
	if(rec.tier <= TIER.ORDINARY && !rec.anchorOnly) n = pushRing(n, rec.ax, rec.ay, hw, hh, rules.ring2, "2");
	if(rec.tier <= TIER.HOVER) n = pushRing(n, rec.ax, rec.ay, hw, hh, rules.ring3, "3");
	for(let i = 0; i < n; i++){
		const c = candidates[i];
		c.leader = pointRectDistance(rec.ax, rec.ay, c.cx - hw, c.cy - hh, c.cx + hw, c.cy + hh) > rules.leaderMin;
	}
	return n;
}

// ---------------------------------------------------------------------------------------------------------------------
// Vurdering af én kandidat

const verdict = {blocked: false, penalty: 0, badness: 0, labelsOnly: false};

// tilstand til gitter-besøgsfunktionen (en statisk funktion i stedet for en closure pr. kandidat)
let gGrid = null, gOwner = null, gSoft = false, gX0 = 0, gY0 = 0, gX1 = 0, gY1 = 0, gOwn = 0, gOwnSoft = 0, gOther = 0;
function visitGeometry(i){
	let hit;
	if(gGrid.type[i] === 0) hit = segmentHitsRect(gGrid.a[i], gGrid.b[i], gGrid.c[i], gGrid.d[i], gX0, gY0, gX1, gY1);
	else hit = circleHitsRect(gGrid.a[i], gGrid.b[i], gGrid.c[i], gX0, gY0, gX1, gY1);
	if(!hit) return;
	if(gGrid.owner[i] === gOwner){
		// et resultat må godt ligge over sit eget objekts tynde linjer (ikke over dets greb): det koster blot, så andre pladser foretrækkes
		if(gSoft && gGrid.type[i] === 0) gOwnSoft++; else gOwn++;
	}else gOther++;
}

function evaluate(rec, cx, cy, hw, hh, env, pad){
	const {W, H, rules} = env;
	const x0 = cx - hw, y0 = cy - hh, x1 = cx + hw, y1 = cy + hh;
	let blocked = false, penalty = 0, badness = 0, otherBlock = false;

	// stille zone om det punkt, der trækkes: kun aktive labels (tier 0) må ligge der
	const q = env.quiet;
	if(q && rec.tier > 0 && rectsOverlapArea(x0, y0, x1, y1, q.x0, q.y0, q.x1, q.y1) > 0){ blocked = true; otherBlock = true; badness += 50; }

	// arbejdsfladens kanter
	const m = rules.margin;
	const outX = Math.max(m - x0, 0, x1 - (W - m));
	const outY = Math.max(m - y0, 0, y1 - (H - m));
	if(outX > 0 || outY > 0){
		blocked = true; otherBlock = true;
		badness += (outX + outY) * 5;
	}

	// faste flader og paneler
	for(const o of env.obstacles){
		const a = rectsOverlapArea(x0 - pad, y0 - pad, x1 + pad, y1 + pad, o.x0, o.y0, o.x1, o.y1);
		if(a > 0){ blocked = true; otherBlock = true; badness += a * 0.2; }
	}

	// allerede placerede labels
	const placed = env.placed;
	for(let i = 0; i < placed.length; i++){
		const p = placed[i];
		const a = rectsOverlapArea(x0 - pad, y0 - pad, x1 + pad, y1 + pad, p.x0, p.y0, p.x1, p.y1);
		if(a > 0){ blocked = true; badness += a * 0.05; }
	}

	// objektgeometri: eget objekts linjer og greb blokerer; andres koster kun lidt
	let ownHits = 0, otherHits = 0;
	if(env.grid){
		gGrid = env.grid; gOwner = rec.owner; gSoft = !!rec.softOwn; gX0 = x0; gY0 = y0; gX1 = x1; gY1 = y1; gOwn = 0; gOwnSoft = 0; gOther = 0;
		gGrid.query(x0, y0, x1, y1, visitGeometry);
		ownHits = gOwn; otherHits = gOther; penalty += rec.anchorOnly ? 0 : Math.min(gOwnSoft, 6) * 4; // hjælpelabels hører til linjen og straffes ikke for at ligge på den
	}
	if(ownHits > 0){ blocked = true; otherBlock = true; badness += ownHits * 100; }
	penalty += Math.min(otherHits, 6) * 4;
	badness += otherHits;

	verdict.blocked = blocked; verdict.penalty = penalty; verdict.badness = badness;
	verdict.labelsOnly = blocked && !otherBlock; // blokeret alene af andre labels (kan være forbigående)
	return verdict;
}

// ---------------------------------------------------------------------------------------------------------------------
// Selve løsningen

const sortScratch = [];

// Prioritet først (en tidligere synlig, lavt prioriteret label blokerer aldrig et vigtigere resultat); inden for samme prioritet går det valgte
// objekts labels forud, derefter dem, der allerede står - den ældste først (anciennitet: en label, der er kommet til senere, kan aldrig fortrænge
// en ældre, så to labels skifter ikke skiftevis plads, når deres størrelser svinger) - derefter objektets størrelse som sekundært kriterium.
let layoutSeq = 0;
function compareRecs(a, b){
	if(a.tier !== b.tier) return a.tier - b.tier;
	if((a.boost | 0) !== (b.boost | 0)) return (b.boost | 0) - (a.boost | 0);
	if(a.prevVisible !== b.prevVisible) return a.prevVisible ? -1 : 1;
	if(a.prevVisible && a.shownSince !== b.shownSince) return a.shownSince - b.shownSince;
	if((a.priority || 0) !== (b.priority || 0)) return (b.priority || 0) - (a.priority || 0);
	if(a.centerDist !== b.centerDist) return a.centerDist - b.centerDist;
	return a.order - b.order;
}

// recs: labels med rec.maxLevel > 0 (de øvrige er allerede skjult af zoomreglerne) og rec.onScreen.
// Felter pr. rec: id, order, owner, kind ("result"|"segment"|"vertex"), tier, forced, maxLevel, sizes[LEVEL] = {w,h},
//   ax, ay, edge ({x0,y0,x1,y1} eller null), centerDist, og tilstand fra forrige layout: visible (prevVisible), level, slot.
// env: {W, H, rules, obstacles: [{x0,y0,x1,y1}], grid: GeometryGrid|null, placed: []}
// Skriver: rec.visible, rec.level, rec.cx, rec.cy, rec.leader, rec.slot, rec.reason. Returnerer antal synlige.
export function solveLayout(recs, env){
	const rules = env.rules || RULES;
	env.rules = rules;
	env.placed = env.placed || [];
	env.placed.length = 0;

	sortScratch.length = 0;
	layoutSeq++;
	for(const rec of recs){
		rec.prevVisible = !!rec.visible;
		if(rec.prevVisible && !(rec.shownSince > 0)) rec.shownSince = layoutSeq; // synlig uden kendt start (fx første layout): nu
		rec.prevLevel = rec.level | 0;
		rec.prevSlot = rec.slot || null;
		sortScratch.push(rec);
	}
	sortScratch.sort(compareRecs);

	let visibleCount = 0;

	for(const rec of sortScratch){
		rec.visible = false; rec.level = LEVEL.HIDDEN; rec.slot = null; rec.leader = false; rec.reason = "";

		let placedOk = false;
		let fallback = null; // mindst ringe pladsering, hvis labelen må vises uanset

		for(let level = rec.maxLevel; level >= LEVEL.COMPACT && !placedOk; level--){
			const size = rec.sizes[level];
			if(!size) continue;
			const hw = size.w / 2, hh = size.h / 2;
			// en label, der var skjult/mindre, vender først tilbage med ekstra luft (ingen flimren)
			const pad = rules.pad + ((!rec.prevVisible || rec.prevLevel < level) ? rules.reshowPad : 0);
			const n = buildCandidates(rec, size, rules);

			let best = -1, bestCost = Infinity;

			// en tidligere placering bevares, så længe den fortsat er fri (stabilitet = vedvarende pladsforhold). Er den blokeret - af en label,
			// et panel, et greb eller kanten - søges straks en ny; der ventes ikke.
			if(rec.prevVisible && rec.prevLevel === level && rec.prevSlot){
				for(let i = 0; i < n; i++){
					const c = candidates[i];
					if(c.key !== rec.prevSlot) continue;
					if(!evaluate(rec, c.cx, c.cy, hw, hh, env, pad).blocked) best = i;
					break;
				}
			}

			// ellers den første brugbare uden straf (kandidaterne står i prioriteret rækkefølge), ellers den med mindst straf
			if(best < 0){
				for(let i = 0; i < n; i++){
					const c = candidates[i];
					const v = evaluate(rec, c.cx, c.cy, hw, hh, env, pad);

					if(rec.forced && (!fallback || v.badness < fallback.badness)){
						if(!fallback) fallback = {};
						fallback.badness = v.badness; fallback.level = level; fallback.cx = c.cx; fallback.cy = c.cy;
						fallback.key = c.key; fallback.leader = c.leader; fallback.hw = hw; fallback.hh = hh;
					}
					if(v.blocked) continue;

					const cost = v.penalty + i * 0.5;
					if(cost < bestCost){ bestCost = cost; best = i; }
					if(v.penalty === 0) break;
				}
			}
			if(best < 0) continue;

			const c = candidates[best];
			acceptPlacement(rec, level, c.cx, c.cy, c.key, c.leader, hw, hh, env);
			placedOk = true;
		}

		if(!placedOk && rec.forced && fallback){
			// nødvendige oplysninger skjules ikke: den mindst dårlige plads, rykket ind i arbejdsfladen
			const m = rules.margin;
			const cx = Math.min(Math.max(fallback.cx, m + fallback.hw), Math.max(m + fallback.hw, env.W - m - fallback.hw));
			const cy = Math.min(Math.max(fallback.cy, m + fallback.hh), Math.max(m + fallback.hh, env.H - m - fallback.hh));
			acceptPlacement(rec, fallback.level, cx, cy, fallback.key + "!", fallback.leader, fallback.hw, fallback.hh, env);
			rec.reason = "forced";
			placedOk = true;
		}

		if(placedOk){
			visibleCount++;
			if(!rec.prevVisible) rec.shownSince = layoutSeq;
		}else{
			rec.shownSince = 0;
			rec.reason = rec.reason || "space";
		}
	}

	return visibleCount;
}

function acceptPlacement(rec, level, cx, cy, key, leader, hw, hh, env){
	rec.visible = true; rec.level = level; rec.cx = cx; rec.cy = cy; rec.slot = key; rec.leader = leader;
	env.placed.push({x0: cx - hw, y0: cy - hh, x1: cx + hw, y1: cy + hh});
}
